import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as catalogApi from "../../api/catalogApi";
import { getRequestName, requestSong, voteRequest } from "../../actions/requestsActions";
import { selectAskedSongs, selectRequests } from "../../selectors";
import { songMeta } from "../common/SongRow";
import PreviewRow from "./PreviewRow";
import SongPreview from "./SongPreview";

const DEBOUNCE_MS = 350;
let searchSequence = 0;

const asking = (votes) => `${votes || 1} asking`;

/**
 * Requesting, over the guest's live page: what others asked for already (with a +1), a
 * search for anything else, and the guest's name. A tap on a row previews the song.
 */
const RequestSheet = ({ onClose }) => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const requests = useSelector(selectRequests);
  const mine = useSelector(selectAskedSongs);
  const queued = useSelector((state) => state.songs);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState({ term: "", songs: null }); // the last answer, for the term it answers
  const [name, setName] = useState(getRequestName);
  const [preview, setPreview] = useState(null); // { songId, title, held }

  const term = query.trim();
  useEffect(() => {
    if (!term) return undefined;
    const sequence = ++searchSequence;
    const handle = setTimeout(() => {
      catalogApi
        .search(term)
        .then((songs) => sequence === searchSequence && setFound({ term, songs }))
        .catch(() => sequence === searchSequence && setFound({ term, songs: [] }));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [term]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !preview) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, preview]);

  const results = term && found.term === term ? found.songs : null;

  const previewOf = (song) => (state) => setPreview(state ? { songId: song.Id, title: song.Title, held: state.held } : null);
  const previewOfRequest = (request) => (state) => setPreview(state ? { songId: request.SongId, title: request.SongTitle, held: state.held } : null);

  const ask = async (song) => {
    if (await dispatch(requestSong(session.Code, song, name))) onClose();
  };

  const statusOf = (song) => {
    if (song.Id === session.SongId) return "Live";
    if (queued[song.Id]) return "In set ✓";
    if (mine.includes(song.Id)) return "Asked ✓";
    return null;
  };

  const subOf = (song) => {
    if (song.Id === session.SongId) return "On screens now";
    if (queued[song.Id]) return "Already in the set";
    return songMeta(song) || "Tap to read it";
  };

  return (
    <>
      <div className="sheet sheet-over" role="dialog" aria-label="Request a song" data-screen-label="Guest request">
        <div className="sheet-head">
          <span className="sheet-title">Request a song</span>
          <button type="button" className="btn btn-round btn-xs btn-outline" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="search" style={{ marginBottom: 12, flex: "none" }}>
          <span className="glyph" aria-hidden="true">
            ⌕
          </span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Title, lyric, or writer" aria-label="Search songs" autoComplete="off" />
          {query && (
            <button type="button" className="clear" onClick={() => setQuery("")} aria-label="Clear the search">
              ×
            </button>
          )}
        </div>
        <div className="sheet-body">
          {!term ? (
            <>
              {requests.length > 0 && (
                <div className="kicker" style={{ padding: "2px 4px 4px" }}>
                  Already asked for
                </div>
              )}
              {requests.map((request) => (
                <div key={request.Id} className="sheet-result">
                  <PreviewRow title={request.SongTitle} sub={`${request.RequestedBy ? `asked by ${request.RequestedBy}` : "asked"} · ${asking(request.Votes)}`} onPreview={previewOfRequest(request)} />
                  {mine.includes(request.SongId) ? (
                    <span className="btn btn-xs btn-outline">Asked ✓</span>
                  ) : (
                    <button type="button" className="btn btn-xs btn-primary" onClick={() => dispatch(voteRequest(session.Code, request))} aria-label={`Add your voice for ${request.SongTitle}`}>
                      +1
                    </button>
                  )}
                </div>
              ))}
              {requests.length === 0 && <div className="sheet-note sheet-note-lg">No requests yet. Search for a song to ask for it.</div>}
            </>
          ) : (
            <>
              {(results || []).map((song) => {
                const status = statusOf(song);
                return (
                  <div key={song.Id} className="sheet-result">
                    <PreviewRow title={song.Title} sub={subOf(song)} onPreview={previewOf(song)} />
                    {status ? (
                      <span className="btn btn-xs btn-outline">{status}</span>
                    ) : (
                      <button type="button" className="btn btn-xs btn-primary" onClick={() => ask(song)} aria-label={`Request ${song.Title}`}>
                        Request
                      </button>
                    )}
                  </div>
                );
              })}
              {results && results.length === 0 && <div className="sheet-note sheet-note-lg">Nothing matches. Ask the host by name instead.</div>}
              {results === null && <div className="sheet-note sheet-note-lg">Searching…</div>}
            </>
          )}
        </div>
        <div className="sheet-foot" style={{ flexDirection: "column", gap: 0 }}>
          <input className="field" value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name (optional)" maxLength={50} autoComplete="name" aria-label="Your name" />
          <div className="sheet-note" style={{ padding: "10px 4px 0" }}>
            It lands in the host’s set list as a one-tap decision. Your name is optional.
          </div>
        </div>
      </div>
      {preview && <SongPreview songId={preview.songId} title={preview.title} held={preview.held} onClose={() => setPreview(null)} />}
    </>
  );
};

export default RequestSheet;
